import { Box, SelectChangeEvent, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { SelectInput } from '@/components/form/baseInputs/SelectInput/SelectInput';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { GroupedHeaderList } from '@/components/lists/GroupedHeaderList/GroupedHeaderList';
import { useFormContext } from '@/components/react-hook-form';
import { COLORS } from '@/styles/tokens/colors';
import { DispositiveProvisionTransferTaxKind } from '@/types/schema';

import { getTransferTaxKindOptions } from '../DispositiveProvisionsForm.constants';
import {
  SelectedRecipient,
  SplitDispositionFormShape,
} from './SplitDispositionModal.types';
import { formatSplitFraction } from './SplitDispositionModal.utils';

const RECIPIENT_TYPE_COLUMN_WIDTH = 200;
const ACTION_COLUMN_WIDTH = 32;

interface SplitRecipientsTableProps {
  hasStateTax?: boolean;
}

export function SplitRecipientsTable({
  hasStateTax = false,
}: SplitRecipientsTableProps) {
  const { control, setValue } = useFormContext<SplitDispositionFormShape>();

  const recipients = useWatch({ control, name: 'selectedRecipients' });
  const distributionKind = useWatch({ control, name: 'distributionKind' });
  const totalValue = useWatch({ control, name: 'distributionValue' });

  const recipientCount = recipients?.length ?? 0;

  const handleTransferTaxKindChange = useCallback(
    (id: string, event: SelectChangeEvent<unknown>) => {
      if (!recipients) return;
      const updated = recipients.map((r) =>
        r.id === id
          ? {
              ...r,
              transferTaxKind: event.target.value as
                | DispositiveProvisionTransferTaxKind
                | '',
            }
          : r
      );
      setValue('selectedRecipients', updated);
    },
    [recipients, setValue]
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (!recipients) return;
      setValue(
        'selectedRecipients',
        recipients.filter((r) => r.id !== id)
      );
    },
    [recipients, setValue]
  );

  const splitPercentage = useMemo(
    () =>
      recipientCount > 0
        ? new Decimal(100).dividedBy(recipientCount)
        : new Decimal(0),
    [recipientCount]
  );

  const splitAmount = useMemo(
    () =>
      distributionKind === 'amount' && totalValue && recipientCount > 0
        ? totalValue.dividedBy(recipientCount)
        : null,
    [distributionKind, totalValue, recipientCount]
  );

  const transferTaxOptions = getTransferTaxKindOptions({ hasStateTax });

  if (!recipients || recipients.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          p: '12px',
          bgcolor: COLORS.GRAY[100],
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body-xs">Recipient</Typography>
        </Box>
        <Box sx={{ width: RECIPIENT_TYPE_COLUMN_WIDTH }}>
          <Typography variant="body-xs">Recipient type</Typography>
        </Box>
        <Box sx={{ width: ACTION_COLUMN_WIDTH }} />
      </Stack>

      <Divider sx={{ borderColor: COLORS.GRAY[200] }} />

      <Box pt={1.5}>
        <GroupedHeaderList<SelectedRecipient>
          rowGroups={[
            {
              items: recipients,
              noItemsText: 'No recipients selected',
            },
          ]}
          withDividers
          ItemComponent={({ item }) => (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
                <Stack>
                  <Typography variant="body-s">{item.displayName}</Typography>
                  <Typography variant="body-xs">
                    {splitAmount
                      ? `$${splitAmount.toFixed(2)}`
                      : formatSplitFraction(recipientCount, splitPercentage)}
                  </Typography>
                </Stack>
              </Stack>
              <Box sx={{ width: RECIPIENT_TYPE_COLUMN_WIDTH }}>
                <SelectInput
                  label="Recipient type"
                  hideLabel
                  value={item.transferTaxKind}
                  options={transferTaxOptions}
                  onChange={(e) => handleTransferTaxKindChange(item.id, e)}
                />
              </Box>
              <IconButton
                icon={Trash01Icon}
                ariaLabel="Remove recipient"
                size="xs"
                variant="transparent"
                onClick={() => handleRemove(item.id)}
              />
            </Stack>
          )}
        />
      </Box>
    </Stack>
  );
}
